import React from "react";
import { ShieldCheck, LockKeyhole, Globe2, Mail, MapPin } from "lucide-react";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-slate-50 to-blue-50 py-16 px-4">
      <div className="max-w-5xl mx-auto bg-white p-10 rounded-3xl shadow-2xl border border-blue-100 backdrop-blur-lg">

        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-bold text-blue-700 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600 text-lg">
            Last Updated: March 16, 2026
          </p>
        </div>

        <p className="text-gray-700 mb-6">
          At <strong>S Infinite Express PVT. LTD.</strong>, we are committed to
          protecting your privacy and ensuring that your personal data is
          handled safely and responsibly. This Privacy Policy explains how we
          collect, use, and protect your information when you use our logistics
          and freight services through our website or mobile application.
        </p>

        {/* What We Collect */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-blue-600 font-semibold text-xl">
            <ShieldCheck className="w-6 h-6" />
            What We Collect
          </div>

          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
            <li>Full name, company name, contact number, and email address</li>
            <li>Pickup and delivery addresses</li>
            <li>Shipment tracking IDs and order details</li>
            <li>GST information and billing details</li>
            <li>Device information such as IP address, device type, and browser</li>
            <li>
              Location data (precise or approximate location from your device)
              when you grant location permission in the mobile application
            </li>
          </ul>
        </div>

        {/* Location Data */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-blue-600 font-semibold text-xl">
            <MapPin className="w-6 h-6" />
            Location Data
          </div>

          <p className="text-gray-700 mb-2">
            Our mobile application may access your device’s location (GPS or
            network-based location) if you allow location permissions.
          </p>

          <p className="text-gray-700 mb-2">Location data may be used to:</p>

          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
            <li>Automatically detect your current pickup location</li>
            <li>Help you select accurate pickup and delivery addresses</li>
            <li>Improve route planning for logistics and transport</li>
            <li>Enable shipment tracking and delivery coordination</li>
            <li>Improve service quality and operational efficiency</li>
          </ul>

          <p className="text-gray-700 mt-3">
            Location data is collected only when you grant permission and while
            you actively use the mobile application (foreground usage). You can
            disable location access anytime through your device settings.
          </p>
        </div>

        {/* How We Use Data */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-blue-600 font-semibold text-xl">
            <LockKeyhole className="w-6 h-6" />
            How We Use Your Information
          </div>

          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
            <li>To schedule and manage shipments</li>
            <li>To provide shipment tracking and logistics services</li>
            <li>To send order updates, alerts, and notifications</li>
            <li>For billing, invoicing, and tax compliance</li>
            <li>To improve our logistics platform and customer experience</li>
            <li>To comply with legal and regulatory obligations</li>
          </ul>
        </div>

        {/* Data Sharing */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-blue-600 font-semibold text-xl">
            <Globe2 className="w-6 h-6" />
            Data Sharing & Third Parties
          </div>

          <p className="text-gray-700">
            We do <strong>not</strong> sell or rent your personal information to
            third parties. However, we may share limited information with
            trusted partners when necessary to provide our services.
          </p>

          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2 mt-2">
            <li>Courier and transport vendors to complete deliveries</li>
            <li>Payment gateways and invoicing providers</li>
            <li>Technology partners supporting our logistics platform</li>
            <li>Government or legal authorities when required by law</li>
          </ul>

          <p className="text-gray-700 mt-2">
            Location data may be shared with delivery partners only when
            necessary to perform pickup or delivery services.
          </p>
        </div>

        {/* Security */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-blue-600 font-semibold text-xl">
            <LockKeyhole className="w-6 h-6" />
            Data Security
          </div>

          <p className="text-gray-700">
            We use encryption, secure cloud infrastructure, and strict access
            controls to protect your information. Our systems are regularly
            monitored and reviewed to prevent unauthorized access, misuse, or
            data breaches.
          </p>
        </div>

        {/* User Rights */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-blue-600 font-semibold text-xl">
            <ShieldCheck className="w-6 h-6" />
            Your Rights
          </div>

          <p className="text-gray-700">
            You have the right to request access, correction, or deletion of
            your personal information. You may also disable location
            permissions at any time through your device settings.
          </p>
        </div>

        {/* Contact */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-blue-600 font-semibold text-xl">
            <Mail className="w-6 h-6" />
            Contact Us
          </div>

          <p className="text-gray-700">
            If you have any questions about this Privacy Policy or wish to
            request access or deletion of your data, please contact us:
          </p>

          <p className="mt-2 text-gray-800">
            📧 Email:{" "}
            <a
              href="mailto:support@sinfiniteexpress.com"
              className="text-blue-600 underline"
            >
              support@sinfiniteexpress.com
            </a>
          </p>

          <p className="text-gray-800">
            📞 Phone: +91 82373 23531
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} S Infinite Express PVT. LTD. All rights
          reserved.
        </div>
      </div>
    </div>
  );
}
